import { useRef, useState, type FormEvent } from 'react';
import { Info, MessageSquare, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  RecentSignInRequiredError,
  useAuth,
  type PendingSmsCode,
  type SecondFactorKind,
} from '@/auth';
import { messages } from '@/i18n';
import { CodeField, ErrorAlert } from './mfaShared';
import { INPUT_CLASS, isCompleteCode, secondFactorErrorMessage, toE164 } from './mfaUtils';
import { ReauthenticateForm } from './ReauthenticateForm';

/** Enroll a recovery phone: number, invisible reCAPTCHA, then the SMS code. */
export function PhoneEnrollmentStep({
  onEnrolled,
  onCancel,
  reauthPreferKind,
}: {
  onEnrolled: () => void;
  onCancel?: () => void;
  reauthPreferKind?: SecondFactorKind | undefined;
}) {
  const t = messages.mfa;
  const { sendEnrollmentSms, confirmPhoneEnrollment } = useAuth();
  const [phone, setPhone] = useState('');
  const [pendingSms, setPendingSms] = useState<PendingSmsCode | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [restarted, setRestarted] = useState(false);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  const fail = (err: unknown) => {
    if (err instanceof RecentSignInRequiredError) {
      setNeedsReauth(true);
      return;
    }
    setError(secondFactorErrorMessage(err));
  };

  if (needsReauth) {
    return (
      <ReauthenticateForm
        preferKind={reauthPreferKind}
        onDone={() => {
          // The code already sent belongs to the old session; send a new one.
          setNeedsReauth(false);
          setPendingSms(null);
          setCode('');
          setError(null);
          setRestarted(true);
        }}
        onCancel={() => {
          setNeedsReauth(false);
          onCancel?.();
        }}
      />
    );
  }

  const handleSend = (event: FormEvent) => {
    event.preventDefault();
    if (!recaptchaRef.current) return;
    setError(null);
    setBusy(true);
    sendEnrollmentSms(toE164(phone), recaptchaRef.current)
      .then((next) => {
        setPendingSms(next);
        setRestarted(false);
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const handleConfirm = (event: FormEvent) => {
    event.preventDefault();
    if (!pendingSms || !isCompleteCode(code)) return;
    setError(null);
    setBusy(true);
    confirmPhoneEnrollment(pendingSms, code, t.phoneDefaultLabel)
      .then(() => {
        setBusy(false);
        onEnrolled();
      })
      .catch((err: unknown) => {
        setBusy(false);
        setCode('');
        fail(err);
      });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">{t.phoneStepTitle}</h2>
        <p className="text-muted-foreground mt-1 text-sm">{t.phoneStepBody}</p>
      </div>

      {restarted ? (
        <Alert>
          <Info aria-hidden />
          <AlertDescription>{t.phoneRestarted}</AlertDescription>
        </Alert>
      ) : null}

      <ErrorAlert message={error} />

      {pendingSms ? (
        <form onSubmit={handleConfirm} className="flex flex-col gap-4" noValidate>
          <p className="text-muted-foreground text-sm">
            {t.phoneCodeSent.replace('{phone}', toE164(phone))}
          </p>
          <CodeField value={code} onChange={setCode} disabled={busy} />
          <Button type="submit" className="w-full" disabled={busy || !isCompleteCode(code)}>
            <ShieldCheck aria-hidden />
            {busy ? t.verifying : t.verify}
          </Button>
          <button
            type="button"
            className="text-accent text-center text-xs hover:underline"
            onClick={() => {
              setPendingSms(null);
              setCode('');
              setError(null);
            }}
            disabled={busy}
          >
            {t.changeNumber}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSend} className="flex flex-col gap-4" noValidate>
          <label className="flex flex-col gap-1 text-sm">
            {t.phoneLabel}
            <input
              type="tel"
              required
              autoComplete="tel"
              inputMode="tel"
              placeholder={t.phonePlaceholder}
              aria-describedby="mfa-phone-hint"
              className={INPUT_CLASS}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            />
            <span id="mfa-phone-hint" className="text-muted-foreground text-xs">
              {t.phoneHint}
            </span>
          </label>
          <div className="flex gap-2">
            {onCancel ? (
              <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
                {t.cancel}
              </Button>
            ) : null}
            <Button type="submit" className="flex-1" disabled={busy || !phone.trim()}>
              <MessageSquare aria-hidden />
              {busy ? t.sendingSms : t.sendCode}
            </Button>
          </div>
        </form>
      )}

      {/* The provider renders an invisible reCAPTCHA in here. */}
      <div ref={recaptchaRef} />
    </div>
  );
}
